import React from 'react';
import { MapPin, Phone, Mail, Instagram, Facebook, Clock, Heart } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.footer 
      className="relative bg-deep-50 border-t border-ocean-500/20 text-white overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {/* Animated Wave Background */}
      <div className="absolute inset-0 opacity-10">
        <svg
          className="absolute bottom-0 w-full h-64"
          viewBox="0 0 1440 320"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="url(#wave-gradient)"
            d="M0,256L48,240C96,224,192,192,288,186.7C384,181,480,203,576,218.7C672,235,768,245,864,234.7C960,224,1056,192,1152,181.3C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
          <defs>
            <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#059669" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#0891b2" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#059669" stopOpacity="0.3" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-16">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
        >
          {/* Información de contacto */}
          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-bold mb-4 text-ocean-500">Mar de Jade</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-pearl-700 hover:text-jade-500 transition-colors">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">Costa Atlántica, Argentina</span>
              </div>
              <div className="flex items-center space-x-3 text-pearl-700 hover:text-jade-500 transition-colors">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">+54 223 456-7890</span>
              </div>
              <div className="flex items-center space-x-3 text-pearl-700 hover:text-jade-500 transition-colors">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">info@mardejade.com</span>
              </div>
              <div className="flex items-center space-x-3 text-pearl-700">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">Lun - Dom: 8:00 - 22:00</span>
              </div>
            </div>

            {/* Redes sociales */}
            <div className="flex space-x-4 mt-6">
              <motion.a
                href="#"
                className="text-pearl-600 hover:text-jade-500 transition-colors"
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <Instagram className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="#"
                className="text-pearl-600 hover:text-jade-500 transition-colors"
                whileHover={{ scale: 1.2, rotate: -5 }}
                whileTap={{ scale: 0.9 }}
              >
                <Facebook className="w-5 h-5" />
              </motion.a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h4 className="text-lg font-semibold mb-4 text-ocean-500">Navegación</h4>
            <ul className="space-y-2">
              {['Inicio', 'Productos', 'Servicios', 'Contacto'].map((link) => (
                <li key={link}>
                  <motion.a
                    href={`#${link.toLowerCase()}`}
                    className="text-pearl-700 hover:text-jade-500 transition-colors text-sm"
                    whileHover={{ x: 5 }}
                  >
                    {link}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Productos */}
          <motion.div variants={itemVariants}>
            <h4 className="text-lg font-semibold mb-4 text-ocean-500">Especialidades</h4>
            <ul className="space-y-2">
              {['Mariscos Frescos', 'Pescados del Día', 'Ceviches', 'Paellas'].map((item) => (
                <li key={item}>
                  <span className="text-pearl-700 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Historia familiar */}
          <motion.div variants={itemVariants}>
            <h4 className="text-lg font-semibold mb-4 text-ocean-500">Nuestra Historia</h4>
            <p className="text-pearl-700 text-sm leading-relaxed">
              Desde 1987, tres generaciones de tradición culinaria marina. 
              <span className="block mt-2 text-jade-500">
                <Heart className="w-4 h-4 inline mr-1" />
                Hecho con amor y pasión
              </span>
              <span className="block mt-1 text-jade-500">
                en Argentina
              </span>
            </p>
          </motion.div>
        </motion.div>

        {/* Copyright */}
        <motion.div 
          className="mt-12 pt-8 border-t border-ocean-500/20 text-center"
          variants={itemVariants}
        >
          <p className="text-pearl-600 text-sm">
            © 2024 Mar de Jade. Todos los derechos reservados.
          </p>
        </motion.div>
      </div>

      {/* Floating Elements animados */}
      <motion.div
        className="absolute bottom-10 right-10 opacity-20"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.4, 0.2]
        }}
        transition={{ duration: 4, repeat: Infinity }}
      >
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-jade-400/30 to-ocean-500/30 blur-xl" />
      </motion.div>

      <motion.div
        className="absolute top-1/3 left-10 opacity-10"
        animate={{
          scale: [1, 0.8, 1],
          opacity: [0.1, 0.3, 0.1]
        }}
        transition={{ duration: 6, repeat: Infinity, delay: 1 }}
      >
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-ocean-400/20 to-jade-400/20 blur-2xl" />
      </motion.div>
    </motion.footer>
  );
};

export default Footer;
